// src/utils/firestorePaths.js

/**
 * This utility centralizes all Firestore paths for the application.
 * By using these functions, we ensure that all parts of the app access
 * the correct data based on the team's containerized structure.
 * This is a critical part of the team isolation and security model.
 */

/**
 * Get the artifact project ID
 * IMPORTANT: This is hardcoded to 'my-real-estate-assistant' for BOTH
 * production and test environments to maintain consistent data structure
 * @returns {string} The artifact project ID
 */
export const getArtifactProjectId = () => 'my-real-estate-assistant';

const getRoot = (appId) => `artifacts/${appId || getArtifactProjectId()}`;

// --- Global Collections ---
// We now point to a sub-collection within a 'global' document for valid pathing.
export const getGlobalTemplatesPath = (appId) => `${getRoot(appId)}/global/data/system_templates`;

// --- Team-Containerized Collections ---

export const getTeamsRoot = (appId) => `${getRoot(appId)}/teams`;
export const getTeamPath = (appId, teamId) => `${getTeamsRoot(appId)}/${teamId}`;

export const getUsersPath = (appId, teamId) => `${getTeamPath(appId, teamId)}/users`;
export const getUserPath = (appId, teamId, emailDocId) => `${getUsersPath(appId, teamId)}/${emailDocId}`;

export const getLeadsPath = (appId, teamId, emailDocId) => `${getUserPath(appId, teamId, emailDocId)}/realEstateLeads`;
export const getLeadPath = (appId, teamId, emailDocId, leadId) => `${getLeadsPath(appId, teamId, emailDocId)}/${leadId}`;

export const getTemplatesPath = (appId, teamId) => `${getTeamPath(appId, teamId)}/templates`;
export const getTemplatePath = (appId, teamId, templateId) => `${getTemplatesPath(appId, teamId)}/${templateId}`;

export const getAnalyticsRoot = (appId, teamId) => `${getTeamPath(appId, teamId)}/analytics`;
export const getCallEventsPath = (appId, teamId) => `${getAnalyticsRoot(appId, teamId)}/callEvents/events`;
export const getUserActivityEventsPath = (appId, teamId) => `${getAnalyticsRoot(appId, teamId)}/userActivity/events`;

// --- Legacy Paths (for migration script only) ---
export const LEGACY = {
  getUsersPath: (appId) => `${getRoot(appId)}/users`,
  getUserPath: (appId, emailDocId) => `${getRoot(appId)}/users/${emailDocId}`,
  getLeadsPath: (appId, emailDocId) => `${getRoot(appId)}/users/${emailDocId}/realEstateLeads`,
  getTemplatesPath: (appId) => `${getRoot(appId)}/templates`,
  getCallEventsPath: (appId) => `${getRoot(appId)}/analytics/callEvents/events`,
  getUserActivityEventsPath: (appId) => `${getRoot(appId)}/analytics/userActivity/events`,
  getTeamsPath: (appId) => `${getRoot(appId)}/teams`,
};
